import type { SvgIconComponent } from '@mui/icons-material'
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import MapRoundedIcon from '@mui/icons-material/MapRounded'
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded'
import SummarizeRoundedIcon from '@mui/icons-material/SummarizeRounded'

import type { Permission } from '@/features/auth/permissions'
import { Permission as Perm } from '@/features/auth/permissions'
import { paths } from '@/routes/paths'

export interface NavItemV2 {
  label: string
  path: string
  icon: SvgIconComponent
  permission?: Permission
  /** true = la ruta ya vive dentro del shell V2 (AppShellV2). false = apunta
   * a una página comercial existente, madura, todavía no migrada a V2 —
   * navegar ahí sale visualmente del shell V2 hacia el AppLayout clásico.
   * Documentado explícitamente para no sorprender: es la decisión "apuntar
   * temporalmente a la página existente si eso es más seguro" en vez de
   * inventar una ruta V2 vacía para algo que ya funciona bien.
   */
  isV2Native: boolean
  // UX-8 -- se retiró `comingSoon`/`comingSoonPhase` (y su rama en
  // SidebarV2): ningún item los usaba desde la consolidación UX-4 a
  // 5 principales + 2 secundarios, todos con página/ruta real. Código
  // muerto eliminado (P2-4, GOVIA_UX8_P2_DECISIONS.md).
}

export interface NavGroupV2 {
  label: string
  items: NavItemV2[]
  /** Administración debe pesar menos visualmente. */
  secondary?: boolean
}

/**
 * UX-4 (GOVIA_UX_SIMPLIFICATION_AUDIT.md) — navegación comercial final
 * del cliente: exactamente 5 espacios principales + 2 secundarios,
 * resultado de consolidar en UX-1/2/3/4 lo que UX-2.1 empezó como
 * "arquitectura completa visible" de 11 items. Cada capacidad retirada
 * del sidebar SIGUE viva — como sección dentro de otro workspace
 * (Alertas/Predicción/Escenarios/Decisiones/Recomendaciones → Riesgos;
 * Evidencia/Monitoreo → Inteligencia; Fuentes/Organización/Usuarios →
 * Configuración) o como redirect de compatibilidad (ver AppRoutes.tsx)
 * — nunca eliminada, solo re-ubicada. Ver GOVIA_UX4_NAVIGATION_
 * RESPONSIVE_REPORT.md para la auditoría completa ruta por ruta.
 */
export const navGroupsV2: NavGroupV2[] = [
  {
    label: 'Principal',
    items: [
      { label: 'Inicio', path: paths.v2, icon: BoltRoundedIcon, isV2Native: true },
      {
        label: 'Inteligencia',
        path: paths.v2Strategy,
        icon: AssignmentRoundedIcon,
        permission: Perm.REPORTS_READ,
        isV2Native: true,
      },
      { label: 'Territorio', path: paths.v2Territory, icon: MapRoundedIcon, isV2Native: true },
      // UX-1→UX-4 — "Riesgos" es hoy el workspace más completo de V2:
      // panorama + alertas + predicción + escenarios + decisiones
      // formales + recomendaciones, todo en un único flujo de
      // divulgación progresiva (ver RisksPage.tsx).
      { label: 'Riesgos', path: paths.v2Risks, icon: ReportProblemRoundedIcon, permission: Perm.REPORTS_READ, isV2Native: true },
      { label: 'GOVIA AI', path: paths.v2Assistant, icon: SmartToyRoundedIcon, permission: Perm.AI_QUERY, isV2Native: true },
    ],
  },
  {
    label: 'Secundario',
    secondary: true,
    items: [
      // UX-5 -- Reportes ahora V2 nativa (antes apuntaba a la ReportsPage
      // clásica bajo AppLayout, ver AppRoutes.tsx).
      { label: 'Reportes', path: paths.reports, icon: SummarizeRoundedIcon, permission: Perm.REPORTS_READ, isV2Native: true },
      { label: 'Configuración', path: paths.settings, icon: SettingsRoundedIcon, isV2Native: true },
    ],
  },
]

export const navItemsV2: NavItemV2[] = navGroupsV2.flatMap((g) => g.items)
